import { useCallback, useState } from 'react';

export interface FileUploadOptions {
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  onFileSelect?: (file: File) => void;
  onError?: (error: string) => void;
  multiple?: boolean;
}

export interface FileUploadReturn {
  selectedFile: File | null;
  selectedFiles: File[];
  isDragOver: boolean;
  error: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDragEnter: (event: React.DragEvent) => void;
  handleDragLeave: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
  clearFile: () => void;
  clearError: () => void;
  removeFile: (index: number) => void;
  setSelectedFiles: (files: File[]) => void;
  truncateFileName: (fileName: string, maxLength?: number) => string;
}

export const useFileUpload = (options: FileUploadOptions = {}): FileUploadReturn => {
  const { acceptedFileTypes = [], maxFileSize, onFileSelect, onError, multiple = false } = options;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      // Check file type
      if (acceptedFileTypes.length > 0) {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const isAcceptedType = acceptedFileTypes.some(
          (type) => type.toLowerCase() === fileExtension || type.toLowerCase() === file.type
        );

        if (!isAcceptedType) {
          const errorMessage = `Please select a file with one of these types: ${acceptedFileTypes.join(', ')}`;
          setError(errorMessage);
          onError?.(errorMessage);
          return false;
        }
      }

      // Check file size
      if (maxFileSize && file.size > maxFileSize) {
        const errorMessage = `File size must be less than ${Math.round(maxFileSize / 1024 / 1024)}MB`;
        setError(errorMessage);
        onError?.(errorMessage);
        return false;
      }

      return true;
    },
    [acceptedFileTypes, maxFileSize, onError]
  );

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];

      for (const file of fileArray) {
        if (validateFile(file)) {
          validFiles.push(file);
        }
      }

      if (validFiles.length > 0) {
        if (multiple) {
          setSelectedFiles((prev) => [...prev, ...validFiles]);
        } else {
          setSelectedFile(validFiles[0]);
          setSelectedFiles([validFiles[0]]);
        }

        // Call onFileSelect for each valid file
        validFiles.forEach((file) => onFileSelect?.(file));
        setError(null);
      }
    },
    [validateFile, multiple, onFileSelect]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const removeFile = useCallback((indexToRemove: number) => {
    setSelectedFiles((prev) => {
      const newFiles = prev.filter((_, index) => index !== indexToRemove);
      if (newFiles.length > 0) {
        setSelectedFile(newFiles[0]);
      } else {
        setSelectedFile(null);
      }
      return newFiles;
    });
  }, []);

  const setSelectedFilesDirect = useCallback((files: File[]) => {
    setSelectedFiles(files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
    } else {
      setSelectedFile(null);
    }
  }, []);

  const truncateFileName = useCallback((fileName: string, maxLength: number = 20): string => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);
    return `${truncatedName}...${extension}`;
  }, []);

  return {
    selectedFile,
    selectedFiles,
    isDragOver,
    error,
    handleFileChange,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    clearFile,
    clearError,
    removeFile,
    setSelectedFiles: setSelectedFilesDirect,
    truncateFileName,
  };
};
